import { useRef, useState, useEffect } from 'react';
import { Volume2, VolumeX, Play, Pause } from 'lucide-react';

// Traccia royalty-free cinematografica (Pixabay CDN, licenza libera per uso commerciale)
const DEFAULT_BG_MUSIC = 'https://cdn.pixabay.com/download/audio/2022/10/25/audio_ff5567d4b1.mp3';

/**
 * Hero video AgriNexus riutilizzabile.
 * - Autoplay in loop, muto di default (compliance browser autoplay policy)
 * - Toggle audio con un click (fa partire la musica di sottofondo separata)
 * - Fallback gradient statico se il video non è ancora disponibile
 */
export default function SpotHeroVideo({
  src = '/spot-hero.mp4',
  musicSrc = DEFAULT_BG_MUSIC,
  className = '',
  overlayGradient = true,
  aspectClass = 'aspect-[16/9]',
}) {
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const [audioOn, setAudioOn] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [videoAvailable, setVideoAvailable] = useState(true);

  useEffect(() => {
    // Verifica esistenza del file video, altrimenti fallback statico
    fetch(src, { method: 'HEAD' }).then(r => {
      if (!r.ok) setVideoAvailable(false);
    }).catch(() => setVideoAvailable(false));
  }, [src]);

  const toggleAudio = async () => {
    if (!audioRef.current) return;
    if (audioOn) {
      audioRef.current.pause();
      setAudioOn(false);
    } else {
      audioRef.current.volume = 0.35;
      try { await audioRef.current.play(); setAudioOn(true); } catch (_) {}
    }
  };

  const togglePlay = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else { try { await v.play(); setPlaying(true); } catch (_) {} }
  };

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-obsidian ${aspectClass} ${className}`} data-testid="spot-hero-video">
      {videoAvailable ? (
        <video
          ref={videoRef}
          src={src}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="/og-image.png"
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-obsidian via-[#1a1a1a] to-terracotta grid place-items-center">
          <img src="/og-image.png" alt="AgriNexus" className="w-full h-full object-cover opacity-90"/>
        </div>
      )}

      {overlayGradient && (
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 via-obsidian/10 to-transparent pointer-events-none"/>
      )}

      {/* Audio background separato */}
      <audio ref={audioRef} src={musicSrc} loop preload="none"/>

      {/* Controlli */}
      <div className="absolute bottom-4 right-4 flex items-center gap-2 pointer-events-auto">
        <button
          onClick={togglePlay}
          data-testid="spot-video-play-toggle"
          aria-label={playing ? 'Pausa' : 'Riproduci'}
          className="w-10 h-10 rounded-full bg-background/20 backdrop-blur border border-white/20 text-white grid place-items-center hover:bg-background/40 transition"
        >
          {playing ? <Pause size={14}/> : <Play size={14}/>}
        </button>
        <button
          onClick={toggleAudio}
          data-testid="spot-video-audio-toggle"
          aria-label={audioOn ? 'Silenzia musica' : 'Attiva musica'}
          className="h-10 px-4 rounded-full bg-background/20 backdrop-blur border border-white/20 text-white flex items-center gap-2 text-xs font-medium hover:bg-background/40 transition"
        >
          {audioOn ? <><Volume2 size={14}/> Musica</> : <><VolumeX size={14}/> Attiva musica</>}
        </button>
      </div>
    </div>
  );
}
